// responseDecryptionUtil.js
'use strict';

import env from '../resource/env.js';
import { customFetch as customFetchLog } from '../resource/debug.js';

const log = customFetchLog.extend('log:responseDecryptionUtil');

const DEFAULT_CHUNK_SIZE = 16384; // 16KB
const EMPTY_BYTE_ARRAY = new Uint8Array(0);

/**
 * Helper function: add offset to the initial IV (Uint8Array)
 * AES block size is 16 bytes, so offset = total bytes processed / 16
 */
const getShiftedIvForAesCtr = (initialIv, byteOffset) => {
  const iv = new Uint8Array(initialIv);
  const blockOffset = Math.floor(byteOffset / 16);

  // Process big-endian counter from the end of the IV
  let carry = blockOffset;
  for (let i = iv.length - 1; i >= 0; i--) {
    const sum = iv[i] + (carry & 0xff);
    iv[i] = sum & 0xff;
    carry = (carry >>> 8) + (sum >>> 8);
  }
  return iv;
};

/**
 * Get a transform stream that decrypts the data using AES-CTR.
 * @param {Object} {subtleCrypto, cryptoKey, initialIv, chunkSize} - The parameters for the transform stream.
 * @param {SubtleCrypto} {subtleCrypto = globalThis.crypto.subtle} - The subtle crypto object.
 * @param {CryptoKey} {cryptoKey} - The crypto key.
 * @param {Uint8Array} {initialIv} - The initial IV.
 * @param {number} {chunkSize = 16384} - The chunk size.
 * @returns {TransformStream} A transform stream that decrypts the data using AES-CTR.
 */
const getAesCtrDecryptTransformStream = ({
  subtleCrypto = globalThis.crypto.subtle,
  cryptoKey,
  initialIv,
  chunkSize = DEFAULT_CHUNK_SIZE,
}) => {
  if (!cryptoKey || !initialIv) {
    throw new Error(
      `Invalid parameters for getAesCtrDecryptTransformStream(): cryptoKey=${!!cryptoKey}, initialIv=${!!initialIv}`
    );
  }

  let bytesProcessed = 0;
  let leftover = EMPTY_BYTE_ARRAY;
  return new TransformStream({
    async transform(chunk, controller) {
      // combine leftover from previous chunk and current chunk
      let data = new Uint8Array(leftover.length + chunk.length);
      data.set(leftover, 0);
      data.set(chunk, leftover.length);

      let offset = 0;
      // only decrypt the data while the data is larger than the chunk size
      while (offset + chunkSize <= data.length) {
        const segment = data.subarray(offset, offset + chunkSize);
        const decryptedBlock = await subtleCrypto.decrypt(
          {
            name: 'AES-CTR',
            counter: getShiftedIvForAesCtr(initialIv, bytesProcessed),
            length: 64, // common AES-CTR length is 64 bits
          },
          cryptoKey,
          segment
        );
        controller.enqueue(decryptedBlock);

        bytesProcessed += chunkSize;
        offset += chunkSize;
      }

      // store the leftover data for the next chunk
      leftover = data.slice(offset);
    },
    async flush(controller) {
      if (leftover.length > 0) {
        const decryptedBlock = await subtleCrypto.decrypt(
          {
            name: 'AES-CTR',
            counter: getShiftedIvForAesCtr(initialIv, bytesProcessed),
            length: 64, // common AES-CTR length is 64 bits
          },
          cryptoKey,
          leftover
        );
        controller.enqueue(decryptedBlock);
        leftover = EMPTY_BYTE_ARRAY;
      }
    },
  });
};

/**
 * Get decrypted data from given encrypted HTTP response.
 *
 * @param {HTTPResponse} {response} - The encrypted source.
 * @param {string} {preferredReturnType = 'arrayBuffer'} - The type of the returned data. 'arrayBuffer' or 'readableStream'.
 * @returns {Promise} decrypted binary data and content type.
 */
const responseDecryptionUtil = async ({
  response,
  preferredReturnType = 'arrayBuffer',
}) => {
  const initializationVector = response.headers.get('x-encrypted-iv');
  const encryptionAlgorithm = response.headers.get('x-encrypted-algo');
  const encryptionKeyId = response.headers.get('x-encrypted-key-id');

  const hasEncryptionHeaders =
    initializationVector && encryptionAlgorithm && encryptionKeyId;

  const contentType = response.headers.get('x-content-type');
  const chunkSize = Number(
    response.headers.get('x-encrypted-chunk-size') || DEFAULT_CHUNK_SIZE
  );

  log('responseDecryptionUtil()', {
    hasBody: !!response.body,
    hasEncryptionHeaders,
    encryptionAlgorithm,
    encryptionKeyId,
    contentType,
    chunkSize,
  });

  if (
    hasEncryptionHeaders &&
    (response.body || typeof response.arrayBuffer === 'function')
  ) {
    const publicUrlEncryptKeys =
      (
        globalThis.WebApp?.remoteConfig?.PUBLIC_URL_ENCRYPT_KEYS ||
        // globalThis.WebApp.remoteConfig is set in jsBridgeInterfaceSnippet.js from domain tracker
        globalThis.Mdm?.remoteConfig?.PUBLIC_URL_ENCRYPT_KEYS ||
        // globalThis.Mdm.remoteConfig is set in getMdmQueryParamsSnippet.js
        env.PUBLIC_URL_ENCRYPT_KEYS ||
        // ServiceWorkerMessenger updates remoteConfig.data to service worker's globalThis context in updateConfigurations action
        globalThis.remoteConfig?.data?.PUBLIC_URL_ENCRYPT_KEYS ||
        ''
      )
        .split(' ')
        .filter(Boolean) || [];
    const publicUrlEncryptKeyMap = publicUrlEncryptKeys.reduce(
      (keyMap, key) => {
        const [keyId, keyValue] = key.split(':');
        keyMap.set(keyId, keyValue);
        return keyMap;
      },
      new Map()
    );

    log('key lookup', {
      availableKeyIds: [...publicUrlEncryptKeyMap.keys()],
      requestedKeyId: encryptionKeyId,
      hasMatchingKey: publicUrlEncryptKeyMap.has(encryptionKeyId),
    });

    const subtleCrypto =
      globalThis.crypto.subtle || globalThis.crypto.webkitSubtle;
    const cryptoKey = await subtleCrypto.importKey(
      'raw',
      // Backend creates a Buffer with Buffer.from(), which is a subclass of JavaScript's Uint8Array class (https://nodejs.org/api/buffer.html)
      Uint8Array.from(
        atob(publicUrlEncryptKeyMap.get(encryptionKeyId)),
        // atob() returns an binary string, and then we use charCodeAt() to transform it to an array of bytes.
        c => c.charCodeAt(0)
      ),
      encryptionAlgorithm,
      false,
      ['decrypt']
    );

    // AES-CTR
    if ('AES-CTR' === encryptionAlgorithm) {
      log('AES-CTR: starting', { hasResponseBody: !!response.body, chunkSize });

      let bodyStream = response.body;
      if (!bodyStream) {
        // If response.body is null, create one from arrayBuffer
        // This happens in QQ Browser
        const buffer = await response.arrayBuffer();
        log('AES-CTR: response.body is null, using arrayBuffer fallback', {
          byteLength: buffer.byteLength,
        });
        bodyStream = new Blob([buffer]).stream();
      }

      const decryptTransformStream = getAesCtrDecryptTransformStream({
        subtleCrypto,
        cryptoKey,
        initialIv: Uint8Array.from(atob(initializationVector), c =>
          c.charCodeAt(0)
        ),
        chunkSize,
      });
      const decryptedStream = bodyStream.pipeThrough(decryptTransformStream);
      log('AES-CTR: pipeThrough completed', { preferredReturnType });

      if ('readableStream' === preferredReturnType) {
        log('AES-CTR: returning stream');
        return { stream: decryptedStream, contentType };
      } else {
        log('AES-CTR: converting stream to arrayBuffer');
        const response = new Response(decryptedStream, {
          headers: {
            'content-type': contentType,
          },
        });
        const buffer = await response.arrayBuffer();
        log('AES-CTR: arrayBuffer conversion done', {
          byteLength: buffer.byteLength,
        });
        return {
          buffer,
          contentType,
        };
      }
    }

    // AES-CBC or AES-GCM
    log('AES-CBC/GCM: starting decryption', { encryptionAlgorithm });
    const decryptedBuffer = await subtleCrypto.decrypt(
      {
        name: encryptionAlgorithm,
        iv: Uint8Array.from(atob(initializationVector), c => c.charCodeAt(0)),
      },
      cryptoKey,
      await response.arrayBuffer()
    );
    log('AES-CBC/GCM: decryption done', {
      byteLength: decryptedBuffer.byteLength,
    });
    return { buffer: decryptedBuffer, contentType };
  }

  log('no encryption headers, returning raw arrayBuffer');
  return { buffer: await response.arrayBuffer(), contentType };
};

export default responseDecryptionUtil;
